import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, FilePlus2, Users, FileBarChart2, LogOut, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const clientNav = [
  { to: "/dashboard", label: "My Checks", icon: LayoutDashboard, end: true },
  { to: "/dashboard/new-check", label: "New Check", icon: FilePlus2 },
];
const adminNav = [
  { to: "/admin", label: "All Checks", icon: LayoutDashboard, end: true },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/reports", label: "Reports", icon: FileBarChart2 },
];

export default function PortalLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = user?.role === "admin" ? adminNav : clientNav;

  const onLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/"><Logo /></Link>
            <span className="hidden md:inline text-xs font-mono-data uppercase tracking-widest text-slate-500 border-l border-slate-200 pl-4">
              {user?.role === "admin" ? "Admin Portal" : "Client Portal"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex flex-col items-end">
              <span className="text-sm font-semibold text-slate-900" data-testid="portal-user-name">{user?.name}</span>
              <span className="text-xs text-slate-500">{user?.email}</span>
            </div>
            <Link to="/" title="Marketing site">
              <Button variant="ghost" size="icon" className="text-slate-600 hover:bg-slate-100"><Home size={18} /></Button>
            </Link>
            <Button
              data-testid="portal-logout-btn"
              variant="outline"
              onClick={onLogout}
              className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900"
            >
              <LogOut size={16} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto w-full px-5 lg:px-8 py-8 grid lg:grid-cols-[220px_1fr] gap-8 flex-1">
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-1">
            {items.map(({ to, label, icon: Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
                    isActive
                      ? "bg-slate-900 text-white"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`
                }
              >
                <Icon size={16} /> {label}
              </NavLink>
            ))}
          </nav>
        </aside>
        <div className="min-w-0"><Outlet /></div>
      </div>
    </div>
  );
}
